<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Sacar excelentes fotos 360° con tu smartphone requiere dominar unas
            pocas técnicas esenciales que marcan la diferencia entre un
            resultado nítido y profesional y una imagen borrosa mal ensamblada.
            El hardware no es el factor limitante: la técnica y la preparación
            determinan el 95 % de la calidad final. Esta guía cubre todo lo
            necesario para capturar fotos 360° listas para publicar con
            cualquier smartphone moderno.
          </p>
        </section>

        <section>
          <h2>Preparación del equipo antes de la captura</h2>

          <h3>El trípode: imprescindible para buenos resultados</h3>
          <p>
            Sostener el teléfono con la mano durante la captura 360° introduce
            vibraciones que crean desalineaciones entre imágenes y errores de
            stitching visibles. Un trípode estable es el elemento de hardware
            con mayor impacto en la calidad final. Necesitas uno que posicione
            el teléfono a <strong>1,5–1,7 metros de altura</strong> (altura de
            los ojos de pie) y gire suavemente sobre el eje vertical.
          </p>
          <p>
            Un trípode básico de 15–25 € con soporte para smartphone es
            suficiente para fotografía de interiores. Asegúrate de que la cabeza
            esté nivelada antes de empezar.
          </p>

          <h3>Ajustes del teléfono</h3>
          <ul>
            <li>
              <strong>Bloquea la exposición manualmente</strong> — en iPhone,
              mantén pulsado para bloquear AE/AF. En Android, usa el modo Pro
              para fijar ISO y velocidad de obturación.
            </li>
            <li>
              <strong>Desactiva el modo HDR</strong> — el HDR toma múltiples
              capturas por imagen y crea fantasmas en el stitching 360°.
            </li>
            <li>
              <strong>Desactiva la estabilización óptica</strong> — el OIS puede
              introducir movimientos sutiles entre imágenes.
            </li>
          </ul>
        </section>

        <section>
          <h2>Técnica de captura</h2>

          <h3>Posicionamiento central</h3>
          <p>
            Colócate en el centro de la habitación, donde el campo visual sea
            más abierto. Evita situarte demasiado cerca de paredes o muebles
            para evitar distorsiones de perspectiva en la vista 360°.
          </p>

          <h3>Velocidad de giro</h3>
          <p>
            Gira a razón de una imagen cada 2–3 segundos, moviéndote unos 15°
            por paso. Deja que el indicador de captura de la app confirme cada
            zona antes de pasar a la siguiente. No fuerces el avance — espera la
            señal verde.
          </p>

          <h3>Cubrir zonas difíciles</h3>
          <p>
            Travvir detecta las zonas faltantes y te pide que las completes.
            Presta especial atención a las zonas de transición —techo, suelo,
            esquinas— que suelen quedar sin capturar.
          </p>
        </section>

        <section>
          <h2>Gestión de la luz</h2>

          <h3>La luz natural: tu mejor aliada</h3>
          <p>
            Fotografía preferiblemente en días nublados o a mediodía para evitar
            sombras duras y reflejos de sol directo. Abre todas las persianas y
            enciende todas las luces interiores para maximizar y uniformar la
            iluminación.
          </p>

          <h3>Gestionar las ventanas</h3>
          <p>
            Las ventanas muy luminosas sobre un fondo oscuro son el principal
            reto de la foto 360° en interiores. Si es posible, fotografía en
            días nublados o al atardecer cuando la luminosidad exterior se
            acerca a la interior. La IA de Travvir aplica corrección de
            exposición automática para equilibrar los contrastes.
          </p>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿Es realmente necesario un trípode para las fotos 360°?</h3>
          <p>
            Un trípode es muy recomendable para cualquier interior. Sin él, las
            vibraciones de la mano crean errores de stitching visibles,
            especialmente en zonas oscuras.
          </p>

          <h3>¿A qué altura colocar el teléfono?</h3>
          <p>
            La altura estándar es de 1,5–1,7 metros, correspondiente a la altura
            de los ojos de un adulto de pie. Esta posición ofrece la perspectiva
            más natural.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/errores-fotografia-360">10 Errores de Fotografía 360° que Debes Evitar en 2026</a></li>
          <li><a href="/blog/es/corregir-fotos-360-borrosas">Fotos 360° Borrosas: 6 Causas y Soluciones Concretas 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-principiantes">Tour Virtual 360° para Principiantes: Crea tu 1er Tour desde el Móvil</a></li>
          <li><a href="/blog/es/fotografia-interior-360-tour-virtual">Fotografía de Interiores para Tours Virtuales 360°: Guía Técnica</a></li>
          <li><a href="/blog/es/que-es-foto-360">¿Qué es una Foto 360°? Guía Completa 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find((p) => p.slug === "tomar-fotos-360-movil");

useBlogSeo({
  title: "Cómo Tomar Fotos 360° con el Móvil: Técnica Completa 2026 | Travvir",
  description:
    "Domina la técnica de la foto 360° con tu smartphone: trípode, ajustes, velocidad de giro, gestión de la luz. Guía completa para resultados profesionales.",
  keywords:
    "tomar fotos 360 movil, técnica foto 360 smartphone, foto 360 teléfono, ajustes foto 360, trípode foto 360, luz foto 360",
  slug: "tomar-fotos-360-movil",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Es realmente necesario un trípode para las fotos 360°?",
      answer:
        "Muy recomendable. Sin trípode, las vibraciones crean errores de stitching visibles, especialmente en zonas oscuras.",
    },
    {
      question: "¿A qué altura colocar el teléfono?",
      answer:
        "La altura estándar es de 1,5–1,7 metros, correspondiente a la altura de los ojos de un adulto de pie.",
    },
  ],
});

</script>
